import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from '../../../components/shared/Button/Button';
import FormField from '../../../components/shared/FormField/FormField';
import Input from '../../../components/shared/Input/Input';
import SearchableSelect from '../../../components/shared/SearchableSelect/SearchableSelect';
import { bulkGenerateTrips } from '../../../services/trips';
import {
  listTripTemplates, createTripTemplate, deleteTripTemplate,
} from '../../../services/tripTemplates';
import type { TripTemplate, BulkGenerateResult } from '../../../types/api';
import { presetRange, RangePreset } from './bulkSchedule';
import useToast from '../../../hooks/useToast';

interface BulkCreateTripsProps {
  routeOptions: { value: string; label: string }[];
  vehicleOptions: { value: string; label: string }[];
  driverOptions: { value: string; label: string }[];
  optionsLoading: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']; // 0=Mon..6=Sun

export const BulkCreateTrips: React.FC<BulkCreateTripsProps> = ({
  routeOptions, vehicleOptions, driverOptions, optionsLoading, onClose, onCreated,
}) => {
  const toast = useToast();
  const [templates, setTemplates] = useState<TripTemplate[]>([]);
  const [route, setRoute] = useState('');
  const [vehicle, setVehicle] = useState('');
  const [driver, setDriver] = useState('');
  // Default matatu run. Used only when no vehicle is picked — once a vehicle
  // is set, the backend takes the seat count from vehicle.capacity.
  const [expectedCapacity, setExpectedCapacity] = useState<number>(14);
  const [times, setTimes] = useState<string[]>(['08:00']);
  const [days, setDays] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]);
  const [preset, setPreset] = useState<RangePreset>('nextMonth');
  const [start, setStart] = useState(() => presetRange('nextMonth').start);
  const [end, setEnd] = useState(() => presetRange('nextMonth').end);
  const [preview, setPreview] = useState<BulkGenerateResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { listTripTemplates().then(setTemplates).catch(() => setTemplates([])); }, []);

  // Only route + at least one time + a date range are strictly required.
  // Vehicle and driver can be assigned to each trip later via edit.
  const ready = Boolean(route && times.length && start && end);

  const payload = useMemo(() => ({
    route: parseInt(route, 10),
    vehicle: vehicle ? parseInt(vehicle, 10) : null,
    driver: driver ? parseInt(driver, 10) : null,
    expected_capacity: expectedCapacity,
    times, start_date: start, end_date: end, weekdays: days,
  }), [route, vehicle, driver, expectedCapacity, times, start, end, days]);

  useEffect(() => {
    if (!ready) { setPreview(null); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      bulkGenerateTrips({ ...payload, preview: true }).then(setPreview).catch(() => setPreview(null));
    }, 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [ready, payload]);

  const applyPreset = (p: RangePreset) => {
    setPreset(p);
    const r = presetRange(p);
    setStart(r.start); setEnd(r.end);
  };

  const toggleDay = (i: number) =>
    setDays((d) => (d.includes(i) ? d.filter((x) => x !== i) : [...d, i].sort()));

  const setTime = (i: number, val: string) =>
    setTimes((t) => t.map((x, idx) => (idx === i ? val : x)));
  const addTime = () => setTimes((t) => [...t, '12:00']);
  const removeTime = (i: number) => setTimes((t) => t.filter((_, idx) => idx !== i));

  const applyTemplate = (id: string) => {
    const t = templates.find((x) => String(x.id) === id);
    if (!t) return;
    setRoute(String(t.route));
    if (t.vehicle) setVehicle(String(t.vehicle));
    if (t.driver) setDriver(String(t.driver));
    if (t.default_departure_time) setTimes([t.default_departure_time.slice(0, 5)]);
  };

  const saveTemplate = async () => {
    const name = window.prompt('Template name?');
    if (!name) return;
    const created = await createTripTemplate({
      name, route: parseInt(route, 10),
      vehicle: vehicle ? parseInt(vehicle, 10) : null,
      driver: driver ? parseInt(driver, 10) : null,
      default_departure_time: times[0] || null,
    });
    setTemplates((list) => [...list, created]);
  };

  const removeTemplate = async (id: number) => {
    try {
      await deleteTripTemplate(id);
      setTemplates((list) => list.filter((t) => t.id !== id));
    } catch {
      toast.error('Could not delete template.');
    }
  };

  const confirm = async () => {
    setSubmitting(true);
    try {
      const result = await bulkGenerateTrips({ ...payload, preview: false });
      const skipped = result.skipped?.length ?? 0;
      toast.success(`${result.count} trips created${skipped ? `, ${skipped} skipped` : ''}.`);
      onCreated();
    } catch {
      toast.error('Could not create trips. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const count = preview?.count ?? 0;
  const disabled = !ready || count === 0 || Boolean(preview?.capped) || submitting;

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <FormField label="Template" name="template">
            <SearchableSelect
              aria-label="Template" options={templates.map((t) => ({ value: String(t.id), label: t.name }))}
              value="" onChange={applyTemplate} placeholder="Apply a template…" searchPlaceholder="Search templates…"
            />
          </FormField>
        </div>
        <Button variant="secondary" type="button" disabled={!route} onClick={saveTemplate}>Save as template</Button>
      </div>

      {templates.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {templates.map((t) => (
            <span key={t.id} className="flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
              {t.name}
              <button
                type="button"
                aria-label={`Delete template ${t.name}`}
                className="text-slate-400 hover:text-red-600"
                onClick={() => removeTemplate(t.id)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <FormField label="Route" name="route" required>
        <SearchableSelect aria-label="Route" options={routeOptions} value={route} onChange={setRoute}
          loading={optionsLoading} placeholder="Select a route…" searchPlaceholder="Search routes…" />
      </FormField>
      <FormField label="Vehicle (optional — assign later)" name="vehicle">
        <SearchableSelect aria-label="Vehicle" options={vehicleOptions} value={vehicle} onChange={setVehicle}
          loading={optionsLoading} placeholder="Assign later…" searchPlaceholder="Search by registration…" />
      </FormField>
      <FormField label="Driver (optional — assign later)" name="driver">
        <SearchableSelect aria-label="Driver" options={driverOptions} value={driver} onChange={setDriver}
          loading={optionsLoading} placeholder="Assign later…" searchPlaceholder="Search by name or licence…" />
      </FormField>
      <FormField label="Seats per trip" name="expected_capacity">
        <select
          aria-label="Expected capacity"
          className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/20"
          value={expectedCapacity}
          onChange={(e) => setExpectedCapacity(parseInt(e.target.value, 10))}
        >
          <option value={14}>14-seater matatu (default)</option>
          <option value={22}>22-seater</option>
          <option value={33}>33-seater</option>
          <option value={51}>51-seater coach</option>
          <option value={62}>62-seater coach</option>
        </select>
        <p className="mt-1 text-xs text-slate-500">
          Used when no vehicle is chosen. Once a bus is assigned to a trip, its
          real capacity takes over.
        </p>
      </FormField>

      <div>
        <p className="mb-1 text-xs font-medium text-slate-600">Departure times</p>
        <div className="flex flex-wrap items-center gap-2">
          {times.map((t, i) => (
            <span key={i} className="flex items-center gap-1">
              <Input type="time" value={t} onChange={(e) => setTime(i, e.target.value)} aria-label={`Time ${i + 1}`} />
              {times.length > 1 && (
                <button type="button" aria-label={`Remove time ${i + 1}`} className="text-slate-400 hover:text-red-600"
                  onClick={() => removeTime(i)}>×</button>
              )}
            </span>
          ))}
          <Button variant="secondary" type="button" onClick={addTime}>+ add time</Button>
        </div>
      </div>

      <div>
        <p className="mb-1 text-xs font-medium text-slate-600">Date range</p>
        <div className="mb-2 flex flex-wrap gap-2">
          {(['next7', 'next30', 'thisMonth', 'nextMonth'] as RangePreset[]).map((p) => (
            <button key={p} type="button" onClick={() => applyPreset(p)}
              className={`rounded-full px-3 py-1 text-xs font-medium ${preset === p ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-700'}`}>
              {{ next7: 'Next 7', next30: 'Next 30', thisMonth: 'This month', nextMonth: 'Next month' }[p]}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} aria-label="Start date" />
          <span className="text-slate-400">→</span>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} aria-label="End date" />
        </div>
      </div>

      <div>
        <p className="mb-1 text-xs font-medium text-slate-600">Days</p>
        <div className="flex gap-1">
          {WEEKDAYS.map((label, i) => (
            <button key={i} type="button" aria-label={`Day ${i}`} aria-pressed={days.includes(i)}
              onClick={() => toggleDay(i)}
              className={`h-8 w-8 rounded-full text-xs font-semibold ${days.includes(i) ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {preview && (
        <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
          {preview.capped ? (
            <p className="text-red-600">Too many trips — narrow the range.</p>
          ) : (
            <p>Will create <strong>{count}</strong> · {preview.skipped.length} skipped (exist) · {preview.conflicts.length} ⚠ conflicts</p>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="secondary" type="button" onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button type="button" onClick={confirm} loading={submitting} disabled={disabled}>
          {count > 0 ? `Create ${count} trips` : 'Create trips'}
        </Button>
      </div>

    </div>
  );
};
